/**
 * Unsaved Changes Detection Utility
 *
 * Centralized logic for detecting unsaved changes in the task wizard.
 * Handles both create and edit modes with comprehensive field comparison.
 */

import type { Task, TaskGeometry } from '../../../../types';
import type { InlineRequirement } from '../types/data/InlineRequirement';
import type { MantineWizardFormData } from './mantineFormSchema';
import { areGeometryArraysEqual } from '../../../../utils/geometryEditHelpers';

/**
 * Compare requirements arrays for changes
 *
 * Checks if current requirements differ from original requirements.
 * Compares text, completed status, and order.
 */
function requirementsChanged(
  currentRequirements: InlineRequirement[],
  originalRequirements: Task['requirements'] | undefined
): boolean {
  const original = originalRequirements || [];

  // Different lengths means changes
  if (currentRequirements.length !== original.length) {
    return true;
  }

  // Compare each requirement's content
  return currentRequirements.some((current, index) => {
    const originalReq = original[index];
    if (!originalReq) return true;

    return (
      current.text !== originalReq.text ||
      current.completed !== originalReq.completed ||
      current.order !== originalReq.order
    );
  });
}

/**
 * Compare string arrays for changes
 *
 * Used for comparing relatedTaskIds arrays.
 */
function stringArraysChanged(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return true;
  return a.some((item, index) => item !== b[index]);
}

/**
 * Detect unsaved changes in task wizard
 *
 * CREATE mode: Checks if any fields have been filled
 * EDIT mode: Compares current form values with original task data
 *
 * @param formValues - Current form values from Mantine form
 * @param editingTask - Original task (if editing), null/undefined if creating
 * @param currentGeometries - Current geometries from Zustand
 * @param formatForInput - Function to format dates for input comparison
 * @param initialLocationValue - Initial location value (for CREATE mode)
 * @returns true if there are unsaved changes
 */
export function detectUnsavedChanges(
  formValues: MantineWizardFormData,
  editingTask: Task | null | undefined,
  currentGeometries: TaskGeometry[],
  formatForInput: (date: Date | string | null | undefined) => string,
  initialLocationValue: string | null
): boolean {
  if (!editingTask) {
    // CREATE MODE: Check if any fields changed from initial values
    const locationChanged =
      formValues.location?.trim() !== initialLocationValue;

    return (
      formValues.title.trim() !== '' ||
      formValues.description.trim() !== '' ||
      formValues.assignedToId.trim() !== '' ||
      formValues.dueDate?.trim() !== '' ||
      locationChanged ||
      formValues.requirements.length > 0 ||
      formValues.attachments.length > 0 ||
      currentGeometries.length > 0
    );
  }

  // EDIT MODE: Check for changes (including all editable fields and geometries)
  // Use formatForInput for consistent date comparison (locale-aware format)
  const formattedDueDate = formatForInput(editingTask.dueDate);

  return (
    formValues.title !== editingTask.title ||
    formValues.description !== editingTask.description ||
    formValues.assignedToId !== editingTask.assignedToId ||
    formValues.priority !== editingTask.priority ||
    formValues.dueDate !== formattedDueDate ||
    formValues.location !== (editingTask.location || '') ||
    !areGeometryArraysEqual(currentGeometries, editingTask.geometries) ||
    requirementsChanged(formValues.requirements, editingTask.requirements) ||
    stringArraysChanged(
      formValues.relatedTaskIds,
      editingTask.relatedTasks.map((relatedTask) => relatedTask.id)
    ) ||
    formValues.requireRelatedTasksCompletion !==
      editingTask.requireRelatedTasksCompletion
  );
}
